import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Check, Crown, Coins, Repeat, Bitcoin, CreditCard, Loader2, X, PlayCircle, Tag, Clock } from "lucide-react";
import { API, authHeaders } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

const PACK_ICONS = {
  single: Coins,
  pack10: Sparkles,
  monthly: Repeat,
};

export default function Premium() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [ent, setEnt] = useState({ credits: 0, subscription_active: false, subscription_until: null, cancel_at_period_end: false, has_stripe_subscription: false });
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [subActionLoading, setSubActionLoading] = useState(false);
  const [coupon, setCoupon] = useState(null);   // { code, percent_off, expires_at }
  const [couponInput, setCouponInput] = useState(searchParams.get("coupon") || "");
  const [couponLoading, setCouponLoading] = useState(false);

  const refresh = async () => {
    try {
      const [pkgs, e] = await Promise.all([
        axios.get(`${API}/payments/packages`, { headers: authHeaders() }),
        axios.get(`${API}/payments/entitlements`, { headers: authHeaders() }),
      ]);
      setPackages(pkgs.data.packages);
      setEnt(e.data);
    } catch {/* */}
  };

  // Auto-validate ?coupon=... from URL on mount
  useEffect(() => {
    refresh();
    const urlCoupon = searchParams.get("coupon");
    if (urlCoupon) {
      void validateCoupon(urlCoupon.toUpperCase(), { silent: true });
    }
  }, []);

  const validateCoupon = async (code, opts = {}) => {
    const trimmed = (code || "").trim().toUpperCase();
    if (!trimmed) {
      setCoupon(null);
      return;
    }
    setCouponLoading(true);
    try {
      const res = await axios.get(`${API}/payments/coupons/validate?code=${encodeURIComponent(trimmed)}`, { headers: authHeaders() });
      setCoupon(res.data);
      if (!opts.silent) toast.success(t("couponApplied").replace("{p}", res.data.percent_off));
    } catch {
      setCoupon(null);
      if (!opts.silent) toast.error(t("couponInvalid"));
    } finally {
      setCouponLoading(false);
    }
  };

  const buy = async (packageId, withCrypto) => {
    setLoadingPkg(`${packageId}-${withCrypto ? "crypto" : "card"}`);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        with_crypto: withCrypto,
        coupon: coupon ? coupon.code : null,
      }, { headers: authHeaders() });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setLoadingPkg(null);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm(t("confirmCancelSub"))) return;
    setSubActionLoading(true);
    try {
      await axios.post(`${API}/payments/cancel-subscription`, {}, { headers: authHeaders() });
      toast.success(t("subscriptionCanceled"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubActionLoading(false);
    }
  };

  const resumeSub = async () => {
    setSubActionLoading(true);
    try {
      await axios.post(`${API}/payments/resume-subscription`, {}, { headers: authHeaders() });
      toast.success(t("subscriptionResumed"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubActionLoading(false);
    }
  };

  // Compute discounted price for display
  const priceFor = (p) => {
    if (coupon && p.kind === "credit") {
      const reduced = Math.round(p.amount * (100 - coupon.percent_off)) / 100;
      return { original: p.amount, final: reduced, hasDiscount: true };
    }
    return { original: p.amount, final: p.amount, hasDiscount: false };
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <button onClick={() => navigate("/dashboard")} data-testid="premium-back" className="mb-6 inline-flex items-center gap-2 text-sm text-[#a8adbd] transition-colors hover:text-white">
        <ArrowLeft className="h-4 w-4" />{t("backToDashboard")}
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#fdb022]/30 bg-[#fdb022]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#fdb022]">
          <Crown className="h-3.5 w-3.5" />Premium
        </div>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
          {t("premiumTitle")}
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#a8adbd]">{t("premiumSubtitle")}</p>
      </motion.div>

      {/* Entitlements */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="entitlements-card">
        <div className="dc-card p-5">
          <div className="text-xs uppercase tracking-wider text-[#a8adbd]">{t("creditsBalance")}</div>
          <div className="mt-2 font-display text-4xl font-bold dc-gradient-text" data-testid="credits-balance">
            {ent.credits}
          </div>
        </div>
        <div className="dc-card p-5">
          <div className="text-xs uppercase tracking-wider text-[#a8adbd]">{t("subscriptionActive")}</div>
          <div className="mt-2 flex items-center gap-2">
            {ent.subscription_active ? (
              <>
                <span className="relative inline-flex h-3 w-3 items-center justify-center">
                  <span className="absolute h-3 w-3 animate-ping rounded-full bg-[#32d583]/60" />
                  <span className="h-2 w-2 rounded-full bg-[#32d583]" />
                </span>
                <span className="font-display text-base font-semibold text-[#32d583]" data-testid="subscription-status">
                  {t("subscriptionUntil")}: {ent.subscription_until ? new Date(ent.subscription_until).toLocaleDateString() : "—"}
                </span>
              </>
            ) : (
              <span className="font-display text-lg font-semibold text-[#a8adbd]" data-testid="subscription-status">—</span>
            )}
          </div>
          {ent.has_stripe_subscription && (
            <div className="mt-3 space-y-2">
              {ent.cancel_at_period_end ? (
                <>
                  <div className="rounded-xl bg-[#fdb022]/10 px-3 py-2 text-xs text-[#fdb022]" data-testid="cancel-notice">
                    {t("canceledNotice")} {ent.subscription_until ? new Date(ent.subscription_until).toLocaleDateString() : "—"}
                  </div>
                  <button onClick={resumeSub} disabled={subActionLoading} data-testid="resume-subscription-btn"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#32d583]/15 px-4 py-2 text-xs font-semibold text-[#32d583] hover:bg-[#32d583]/25">
                    {subActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                    {t("resumeSubscription")}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-xs text-[#a8adbd]">{t("autoRenews")} · {t("cancelSubscriptionHint")}</div>
                  <button onClick={cancelSub} disabled={subActionLoading} data-testid="cancel-subscription-btn"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#fd267a]/40 bg-[#fd267a]/5 px-4 py-2 text-xs font-semibold text-[#fd267a] hover:bg-[#fd267a]/15">
                    {subActionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                    {t("cancelSubscription")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mb-6 text-sm text-[#a8adbd]">{t("premiumIntro")}</p>

      {/* Coupon banner */}
      <div className="dc-card mb-6 overflow-hidden p-5" data-testid="coupon-section">
        {coupon ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#32d583]/15 text-[#32d583]">
                <Tag className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-white" data-testid="coupon-active-code">
                  {coupon.code} <span className="text-[#32d583]">−{coupon.percent_off}%</span>
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-[#a8adbd]">
                  <Clock className="h-3 w-3" />
                  {t("couponExpires")}: {new Date(coupon.expires_at).toLocaleString()}
                </div>
              </div>
            </div>
            <button onClick={() => { setCoupon(null); setCouponInput(""); }} data-testid="coupon-clear"
              className="rounded-full border border-[#222636] bg-[#0f1117] px-3 py-1.5 text-xs text-[#a8adbd] transition-colors hover:border-[#fd267a]/40 hover:text-white">
              {t("couponClear")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              <Tag className="h-3.5 w-3.5" />{t("couponLabel")}
            </label>
            <input
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), validateCoupon(couponInput))}
              placeholder="GRAD7-XXXXXX"
              data-testid="coupon-input"
              className="flex-1 rounded-full border border-[#222636] bg-[#0f1117] px-4 py-2.5 font-mono text-sm uppercase text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20"
            />
            <button
              onClick={() => validateCoupon(couponInput)}
              disabled={couponLoading || !couponInput}
              data-testid="coupon-apply"
              className="rounded-full dc-gradient-bg px-5 py-2.5 font-display text-sm font-semibold text-white shadow-[0_6px_18px_rgba(253,38,122,0.3)] disabled:opacity-50"
            >
              {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("couponApply")}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3" data-testid="packages-grid">
        {packages.map((p) => {
          const Icon = PACK_ICONS[p.id] || Coins;
          const featured = p.id === "pack10";
          const pr = priceFor(p);
          return (
            <div
              key={p.id}
              className={`relative dc-card overflow-hidden p-6 ${featured ? "ring-2 ring-[#ff4458]/60 shadow-[0_16px_50px_rgba(253,38,122,0.2)]" : ""}`}
              data-testid={`package-${p.id}`}
            >
              {featured && (
                <div className="absolute right-4 top-4 rounded-full dc-gradient-bg px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white">
                  {t("filterPremium")}
                </div>
              )}
              {pr.hasDiscount && (
                <div className="absolute left-4 top-4 rounded-full bg-[#32d583]/20 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-[#32d583]" data-testid={`pkg-discount-badge-${p.id}`}>
                  −{coupon.percent_off}%
                </div>
              )}
              <div className="mb-4 mt-4 inline-flex items-center justify-center rounded-2xl bg-[#222636] p-3 text-[#ff4458]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-bold text-white">{t(`package_${p.id}`)}</h3>
              <p className="mt-1 text-sm text-[#a8adbd]">{t(`package_${p.id}_desc`)}</p>
              <div className="mt-5 flex items-baseline gap-2" data-testid={`pkg-price-${p.id}`}>
                {pr.hasDiscount && (
                  <span className="font-display text-xl font-bold tracking-tighter text-[#6a6f80] line-through" data-testid={`pkg-price-original-${p.id}`}>
                    {pr.original.toFixed(2)}
                  </span>
                )}
                <span className="font-display text-4xl font-bold tracking-tighter dc-gradient-text" data-testid={`pkg-price-final-${p.id}`}>
                  {pr.final.toFixed(2)}
                </span>
                <span className="text-sm font-semibold uppercase text-[#a8adbd]">{p.currency}</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-[#a8adbd]">
                {p.kind === "credit" && (
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#32d583]" />{p.credits} {t("creditsBalance").toLowerCase()}</li>
                )}
                {p.kind === "subscription_month" && (
                  <>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-[#32d583]" />
                      {p.credits} {t("creditsBalance").toLowerCase()} / miesiąc
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-[#32d583]" />
                      Automatyczne odnawianie co miesiąc
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-[#32d583]" />
                      Możliwość anulowania w dowolnym momencie
                    </li>
                  </>
                )}
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#32d583]" />MBTI · Big5 · Attachment</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#32d583]" />PDF · Share link</li>
              </ul>
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => buy(p.id, false)}
                  disabled={loadingPkg !== null}
                  data-testid={`buy-card-${p.id}`}
                  className="flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-4 py-3 font-display text-sm font-semibold text-white shadow-[0_8px_20px_rgba(253,38,122,0.3)] transition-all hover:-translate-y-0.5 disabled:opacity-50"
                >
                  {loadingPkg === `${p.id}-card` ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {t("payWithCard")}
                </button>
                {p.kind !== "subscription_month" && (
                  <button
                    onClick={() => buy(p.id, true)}
                    disabled={loadingPkg !== null}
                    data-testid={`buy-crypto-${p.id}`}
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-[#fdb022]/40 bg-[#fdb022]/10 px-4 py-2.5 font-display text-sm font-semibold text-[#fdb022] transition-all hover:bg-[#fdb022]/15 disabled:opacity-50"
                  >
                    {loadingPkg === `${p.id}-crypto` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bitcoin className="h-4 w-4" />}
                    {t("payWithCrypto")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {ent.credits > 0 && (
        <div className="mt-10 text-center">
          <Link
            to="/analyze/premium"
            data-testid="start-premium-analysis"
            className="inline-flex items-center gap-2 rounded-full dc-gradient-bg px-6 py-3.5 font-display text-base font-semibold text-white shadow-[0_12px_36px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5"
          >
            <Sparkles className="h-5 w-5" />
            {t("startPremium")}
          </Link>
        </div>
      )}
    </div>
  );
}
